import { bandColour, bandPaletteColour, type DialModel, type RollupBand } from "@keydials/dial-renderer";
import type { GeneratedSection, GeneratedStatus } from "@keydials/api-client";

// Illustrative RAG cycle for itemised previews (no live data exists at definition time).

const DAY_PREFIX = "day-";

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * KEY-767: a rolling-days section is a TEMPLATE the live dial shows once per day — today first, each day its own
 * section split into the template's times of day (Morning, Afternoon …), or one whole-day segment when it has none.
 * A preview drawn from the definition shows it the same way, so the author sees the dial people will get rather
 * than the template. Keys follow the server's (`day-2026-10-08`, `day-2026-10-08.morning`); `previewTemplateKeys`
 * maps them back to the template for an editor that selects what was clicked.
 */
export function expandDayTemplates(sections: GeneratedSection[], today: Date = new Date()): GeneratedSection[] {
  if (!sections.some((s) => s.kind === "Days")) return sections;
  const out: GeneratedSection[] = [];
  for (const section of sections) {
    if (section.kind !== "Days") {
      out.push(section);
      continue;
    }
    const slots = section.segments.length > 0
      ? section.segments
      : [{ key: "all-day", label: "All day", name: "All day", items: [] }];
    const count = Math.min(Math.max(section.dayCount ?? 7, 1), 14);
    for (let i = 0; i < count; i++) {
      const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      const key = DAY_PREFIX + isoDate(day);
      const label = i === 0 ? "Today" : i === 1 ? "Tomorrow" : day.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" }).replace(",", "");
      out.push({
        ...section,
        key,
        label,
        segments: slots.map((slot) => ({ ...slot, key: `${key}.${slot.key}`, label: slot.label || slot.name })),
      });
    }
  }
  return out;
}

/** The template keys a preview's day keys stand for (KEY-767): `day-….morning` → (days section, `morning`). Keys
 * that aren't a day's come back unchanged. */
export function previewTemplateKeys(
  sections: GeneratedSection[],
  sectionKey: string,
  segmentKey?: string,
): { sectionKey: string; segmentKey?: string } {
  const template = sections.find((s) => s.kind === "Days");
  if (!template || !sectionKey.startsWith(DAY_PREFIX)) return { sectionKey, segmentKey };
  const slot = segmentKey?.includes(".") ? segmentKey.slice(segmentKey.indexOf(".") + 1) : undefined;
  return { sectionKey: template.key, segmentKey: slot === "all-day" ? undefined : slot };
}
const ILLUSTRATIVE_BANDS: RollupBand[] = ["Green", "Amber", "Red"];

/**
 * Build a renderer DialModel from a definition's structure (shared by AI drafts and
 * persisted definitions). A definition has no live status, so the preview is illustrative:
 * simple dials cycle segment colours through the palette; itemised dials cycle through the
 * RAG bands to show how a rolled-up dial will look.
 */
export function definitionToDialModel(
  definition: {
    statusSet: { statuses: GeneratedStatus[] };
    sections: GeneratedSection[];
    showCentralProgress?: boolean;
    kind?: string;
  },
  // The library palettes a per-segment override can name. When supplied, an itemised segment with an
  // override previews in that palette's colours (KEY-187); omitted → the illustrative RAG colours.
  palettes?: { name: string; statuses: GeneratedStatus[] }[],
): DialModel {
  const palette = definition.statusSet.statuses.map((s) => ({
    key: s.key,
    colour: s.colour,
    order: s.order,
    isTerminal: s.isTerminal,
  }));
  const itemised = definition.kind === "Itemised";
  let counter = 0;
  return {
    innerRadius: 120,
    midRadius: 300,
    outerRadius: 372,
    sectionGap: 0.012,
    showCentralProgress: definition.showCentralProgress,
    // Itemised previews show an illustrative ~50% completion to match the RAG-banded segments.
    completion: itemised ? 0.5 : undefined,
    palette,
    sections: expandDayTemplates(definition.sections).map((section) => ({
      key: section.key,
      label: section.label,
      // KEY-755: the band in the section's own colour, so the preview is the dial the author is making.
      colour: section.colour,
      segments: section.segments.map((segment) => {
        if (itemised) {
          const band = ILLUSTRATIVE_BANDS[counter++ % ILLUSTRATIVE_BANDS.length];
          const override = segment.palette ? palettes?.find((p) => p.name === segment.palette)?.statuses : undefined;
          const colour = override ? bandPaletteColour(band, override) : bandColour(band);
          return { key: segment.key, label: segment.label, name: segment.name, colour, itemStatusKeys: [] };
        }
        const status = palette[counter++ % Math.max(palette.length, 1)];
        return { key: segment.key, label: segment.label, name: segment.name, itemStatusKeys: status ? [status.key] : [] };
      }),
    })),
  };
}
